/**
 * Test ID Constants
 *
 * Centralized data-testid values for the application.
 * Use these constants instead of hardcoded strings to ensure consistency.
 *
 * Usage in components:
 *   <button data-testid={TEST_IDS.HEADER.LANGUAGE_SELECTOR}>...</button>
 *
 * Usage in tests:
 *   When I click the "{TEST_IDS.HEADER.LANGUAGE_SELECTOR}" button
 *   await page.getByTestId(TEST_IDS.HEADER.LANGUAGE_SELECTOR).click();
 *
 * Benefits:
 * - Type-safe with autocomplete
 * - Easy refactoring (rename in one place)
 * - Prevents typos and inconsistencies
 * - Self-documenting code
 */

export const TEST_IDS = {
    // Header/Navigation
    HEADER: {
        LANGUAGE_SELECTOR: 'language-selector-button',
        LANGUAGE_MENU: 'language-menu',
        LANGUAGE_MENU_ITEM: (locale: string) => `language-menu-item-${locale}`,
        MENU_TOGGLE: 'menu-toggle-button',
        LOGO: 'header-logo',
        DARK_MODE_SWITCH: 'dark-mode-switch',
        FULLSCREEN_TOGGLE: 'fullscreen-toggle-button'
    },

    // Widgets
    WIDGETS: {
        SHOP_STATUS: {
            CONTAINER: 'shop-status-widget',
            ITEM: (id: string | number) => `shop-status-item-${id}`
        },
        FAST_LANER: {
            HEALTH_STATE: 'fast-laner-health-state-widget',
            BATTERY_STATE: 'fast-laner-battery-state-widget',
            CHARGING: 'fast-laner-charging-widget'
        }
    },

    // Forms
    FORMS: {
        LOGIN: {
            EMAIL: 'login-email-input',
            PASSWORD: 'login-password-input',
            SUBMIT: 'login-submit-button'
        }
    },

    // Dialogs
    DIALOGS: {
        CONFIRM: 'confirmation-dialog',
        DELETE: 'delete-confirmation-dialog',
        COMPOSE_EMAIL: 'compose-email-dialog'
    },

    // Common UI Elements
    COMMON: {
        LOADING_SPINNER: 'loading-spinner',
        ERROR_MESSAGE: 'error-message',
        SUCCESS_MESSAGE: 'success-message',
        BREADCRUMB: 'breadcrumb'
    }
} as const;

/**
 * Helper type to extract all test ID values
 */
export type TestId = string | ((arg: string | number) => string);

/**
 * Get test ID by path
 *
 * @example
 * getTestId('header', 'language-selector') // Returns 'language-selector-button'
 * getTestId('widgets', 'fast-laner', 'health-state') // Returns 'fast-laner-health-state-widget'
 */
export function getTestId(...path: string[]): string | null {
    let current: any = TEST_IDS;

    for (const segment of path) {
        const upperSegment = segment.toUpperCase().replace(/-/g, '_');
        if (current[upperSegment] !== undefined) {
            current = current[upperSegment];
        } else {
            return null;
        }
    }

    return typeof current === 'string' ? current : null;
}

/**
 * Validate if a test ID exists in constants
 */
export function isValidTestId(testId: string): boolean {
    const allTestIds = getAllTestIds(TEST_IDS);
    return allTestIds.includes(testId);
}

/**
 * Get all test IDs from the constants (recursive)
 */
function getAllTestIds(obj: any, result: string[] = []): string[] {
    for (const key in obj) {
        const value = obj[key];
        if (typeof value === 'string') {
            result.push(value);
        } else if (typeof value === 'object' && value !== null) {
            getAllTestIds(value, result);
        }
    }
    return result;
}

/**
 * All test IDs as a flat array for validation
 */
export const TEST_ID_LIST = getAllTestIds(TEST_IDS);

/**
 * Test ID metadata for documentation and validation
 */
export const TEST_ID_METADATA = {
    'language-selector-button': {
        component: 'Header',
        description: 'Language selector dropdown button',
        location: 'src/layouts/Header.tsx'
    },
    'menu-toggle-button': {
        component: 'Header',
        description: 'Sidebar menu toggle button',
        location: 'src/layouts/Header.tsx'
    },
    'header-logo': {
        component: 'Header',
        description: 'Application logo in header',
        location: 'src/layouts/Header.tsx'
    },
    'dark-mode-switch': {
        component: 'Header',
        description: 'Dark/Light mode toggle switch',
        location: 'src/layouts/Header.tsx'
    },
    'fullscreen-toggle-button': {
        component: 'Header',
        description: 'Fullscreen mode toggle button',
        location: 'src/layouts/Header.tsx'
    },
    'fast-laner-health-state-widget': {
        component: 'FastLanerHealthState',
        description: 'FastLaner health state widget container',
        location: 'src/components/widgets/FastLanerHealthState.tsx'
    }
} as const;

/**
 * Test ID naming conventions
 *
 * General format: {component}-{element}-{type}
 *
 * Examples:
 * - Buttons: 'submit-button', 'cancel-button'
 * - Inputs: 'email-input', 'password-input'
 * - Containers: 'user-profile-container', 'dashboard-widget'
 * - Dynamic: Use functions for IDs with parameters
 *
 * Best practices:
 * 1. Use kebab-case (lowercase with hyphens)
 * 2. Be descriptive but concise
 * 3. Include element type suffix (button, input, dialog, etc.)
 * 4. Group related IDs in nested objects
 * 5. Use functions for dynamic IDs with parameters
 */
